import {
  Body,
  Button,
  Column,
  expandBaseWebBorder,
  expandBorderRadii,
  paddingHorizontal,
  paddingUtil,
  paddingVertical,
  Row,
} from "@gadgetinc/widgets";
import { ErrorCircleIcon } from "@gadgetinc/widgets/src/icons/ErrorCircleIcon";
import { useStyletron } from "baseui";
import React, { useContext, useMemo, useState } from "react";
import type { IncomingOptions, OverwriteGlobalOptions } from "use-http";
import useFetch, { CachePolicies } from "use-http";
import { AddPaymentMethodForm, useAddPaymentForm } from "./Billing/AddPaymentMethodForm";
import { BillingContactCard } from "./Billing/BillingContactCard";
import { BillingContactForm, useBillingContactForm } from "./Billing/BillingContactForm";
import { useBillingSession } from "./Billing/BillingContext";
import { PaymentMethodCard } from "./Billing/PaymentMethodCard";

export interface StaffMasquerade {
  billingAccountId: string;
}

const StaffMasqueradeContext = React.createContext<StaffMasquerade | null>(null);

export const useStaffMasquerade = () => {
  return useContext(StaffMasqueradeContext);
};

export const StaffMasqueradeProvider = (props: { children: React.ReactNode; masqueradeBillingAccountId?: string }) => {
  const { masqueradeBillingAccountId } = props;

  const staffMasquerade = useMemo(() => {
    return masqueradeBillingAccountId
      ? {
          billingAccountId: masqueradeBillingAccountId,
        }
      : null;
  }, [masqueradeBillingAccountId]);

  return <StaffMasqueradeContext.Provider value={staffMasquerade}>{props.children}</StaffMasqueradeContext.Provider>;
};

export const billingApiRoute = (route: string, opts: { searchParams?: URLSearchParams; staffMasquerade?: StaffMasquerade | null } = {}) => {
  const params = opts.searchParams || new URLSearchParams({});
  let path = route;

  if (opts.staffMasquerade) {
    params.append("masqueradeBillingAccountId", opts.staffMasquerade.billingAccountId);
  }

  if (typeof window !== "undefined" && window.GadgetEditConfig) {
    path = path.replace("/auth/api", "/edit/api");
  }

  const searchParams = params.toString();

  return searchParams ? `${path}?${searchParams}` : path;
};

export function useBillingFetch<T>(
  route: string,
  opts?: { searchParams?: URLSearchParams } & (IncomingOptions | OverwriteGlobalOptions),
  deps?: any[]
) {
  const staffMasquerade = useStaffMasquerade();

  const url = useMemo(() => {
    return billingApiRoute(route, { searchParams: opts?.searchParams, staffMasquerade });
  }, [opts?.searchParams, staffMasquerade, route]);

  return useFetch<T>(url, opts, deps);
}

export const useConvergeAssistantCredits = () => {
  const {
    error: convergedCreditsError,
    loading: convergedCreditsLoading,
    data: convergedCredits,
    post: convergeAssistantCredits,
  } = useBillingFetch<{ success: boolean; balance: number }>(`/auth/api/billing/converge-assistant-credits`, {
    cachePolicy: CachePolicies.NETWORK_ONLY,
  });

  return {
    convergedCreditsError,
    convergedCreditsLoading,
    convergedCredits,
    convergeAssistantCredits: async () =>
      await convergeAssistantCredits({
        /* Throws error when called with empty body. Needs empty object param to work */
      }),
  };
};

export const StaffMasqueradeBanner = () => {
  const [_, $theme] = useStyletron();
  const staffMasquerade = useStaffMasquerade();

  if (!staffMasquerade) return null;

  return (
    <Row
      $align="space-between"
      $gap={$theme.sizing.scale300}
      $style={{
        ...paddingVertical($theme.sizing.scale500),
        ...paddingHorizontal($theme.sizing.scale600),
        ...expandBaseWebBorder({
          ...$theme.borders.border300,
          borderColor: $theme.colors.warning400,
        }),
        ...expandBorderRadii($theme.borders.radius200),
        backgroundColor: $theme.colors.warning50,
      }}
    >
      <Row $gap={$theme.sizing.scale300} $style={{ flex: 1 }}>
        <Row
          $style={{
            alignSelf: "stretch",
            alignItems: "flex-start",
            ...paddingHorizontal($theme.sizing.scale100),
            ...paddingVertical($theme.sizing.scale0),
          }}
        >
          <ErrorCircleIcon color={$theme.colors.yellow700} />
        </Row>
        <Body>
          You are masquerading as billing account id {staffMasquerade.billingAccountId}. Remember: with great power comes great
          responsibility.
        </Body>
      </Row>
    </Row>
  );
};

export const useBillingContactElement = ({ inEditor = false }: { inEditor?: boolean } = {}) => {
  const [css, $theme] = useStyletron();
  const [editBillingContact, setEditBillingContact] = useState(false);
  const { form: billingContactForm, submit, submitting } = useBillingContactForm({ inEditor });

  const billingContactElement = useMemo(() => {
    return editBillingContact ? (
      <Column $gap={$theme.sizing.scale500} $align="space-between">
        <Row $align="space-between">
          <Body $style={{ fontWeight: 600 }}>Invoice sent to</Body>
          <Button $style={{ ...paddingUtil(0) }} kind="tertiary" onClick={() => setEditBillingContact(false)}>
            Cancel
          </Button>
        </Row>
        <Column $style={{ alignSelf: "stretch", flex: 1 }}>
          <BillingContactForm
            form={billingContactForm}
            onSubmit={async () => {
              const { success } = await submit();

              if (success) {
                setEditBillingContact(false);
              }
            }}
          />
        </Column>
      </Column>
    ) : (
      <BillingContactCard onEdit={() => setEditBillingContact(true)} />
    );
  }, [$theme, editBillingContact, setEditBillingContact, billingContactForm]);

  return {
    billingContactElement,
    editBillingContact,
    setEditBillingContact,
    billingContactForm,
    submit,
    submitting,
  };
};

export const usePaymentMethodElement = () => {
  const [css, $theme] = useStyletron();
  const billingSession = useBillingSession();
  const [editCard, setEditCard] = useState(false);
  const cardSetupRequired = !billingSession.paymentMethod || editCard;
  const [loadingSetupIntent, setLoadingSetupIntent] = useState(false);
  const { form: addPaymentForm, formComplete, setShowFormIncomplete } = useAddPaymentForm();

  const paymentMethodElement = useMemo(() => {
    return cardSetupRequired ? (
      <Column $gap={$theme.sizing.scale600}>
        <Row $align="space-between">
          <Body
            $style={{
              fontWeight: 600,
            }}
          >
            Payment
          </Body>
          {billingSession.paymentMethod && (
            <Button $style={{ ...paddingUtil(0) }} kind="tertiary" onClick={() => setEditCard(false)}>
              Cancel
            </Button>
          )}
        </Row>
        <AddPaymentMethodForm
          loading={loadingSetupIntent}
          form={addPaymentForm}
          defaultCountry={billingSession.paymentMethod?.customerBillingCountry ?? undefined}
          minHeight="287px"
        />
      </Column>
    ) : (
      <PaymentMethodCard paymentMethod={billingSession.paymentMethod!} onAddCard={() => setEditCard(true)} />
    );
  }, [cardSetupRequired, addPaymentForm, billingSession.paymentMethod, $theme, setEditCard, loadingSetupIntent]);

  return {
    paymentMethodElement,
    editCard,
    setEditCard,
    cardSetupRequired,
    loadingSetupIntent,
    setLoadingSetupIntent,
    formComplete,
    setShowFormIncomplete,
  };
};

export const useCreateSetupIntent = () => {
  const {
    data: setupIntentData,
    post: createSetupIntent,
    loading: loadingSetupIntent,
  } = useBillingFetch<{ clientSecret: string }>("/auth/api/billing/setup-intent", {
    cachePolicy: CachePolicies.NETWORK_ONLY,
  });

  return {
    setupIntentData,
    createSetupIntent,
    loadingSetupIntent,
  };
};
